import { useState } from 'react';
import { ca } from '../i18n/ca';
import type { PlacementReport as Report, PlacementRow } from './useSessionLive';

const BAD = new Set(['floating', 'sunk', 'water', 'destroyed', 'clipping']);

const fmt = (p: number[]) =>
  p.length >= 2
    ? `${Math.round(p[0])}, ${Math.round(p[1])}${p.length >= 3 ? ` · ${p[2].toFixed(1)} m` : ''}`
    : '—';

/** What holds the piece up, when it is a thing with a name: a wall, a table, a building's floor. */
const holder = (r: PlacementRow, stances: Record<string, string>) => {
  if (!r.on || !r.stance) return '';
  if (r.stance === 'floor' && (r.on === 'the terrain' || r.on === 'the sea')) return '';
  return `${stances[r.stance] ?? r.stance} ${r.on}`;
};

/**
 * The runtime's placement report as a table: every object of the base, every
 * slot, vehicle, force and point, with what is under it and how far off it
 * stands. The evidence the viability review asked for in place of
 * screenshots (VIABILITY_REVIEW.md §6, phase 1). A row is wrong when it
 * floats, sank, drowned or blew up; a force "moved" is the ground's answer,
 * not a fault.
 */
export function PlacementReport({ report }: { report: Report | null }) {
  const t = ca.placement;
  const [onlyProblems, setOnlyProblems] = useState(false);
  if (!report) {
    return (
      <section className="card" aria-label={t.title}>
        <h2>{t.title}</h2>
        <p className="muted small">{t.none}</p>
      </section>
    );
  }
  const rows = onlyProblems ? report.rows.filter((r) => BAD.has(r.outcome)) : report.rows;
  const rowClass = (r: PlacementRow) =>
    BAD.has(r.outcome) ? 'bad' : r.outcome === 'moved' ? 'muted' : '';
  return (
    <section className="card" aria-label={t.title}>
      <div className="row between">
        <h2>{t.title}</h2>
        <span className={`chip ${report.off + report.water > 0 ? 'warn' : 'ok'}`}>
          {t.summary(report.total, report.off, report.water)}
        </span>
      </div>
      <p className="muted small">{t.intro}</p>
      <label className="small">
        <input
          type="checkbox"
          checked={onlyProblems}
          onChange={(e) => setOnlyProblems(e.target.checked)}
        />{' '}
        {t.onlyProblems}
      </label>
      <div className="table-wrap">
        <table className="data placement">
          <thead>
            <tr>
              <th>{t.columns.kind}</th>
              <th>{t.columns.id}</th>
              <th>{t.columns.class}</th>
              <th>{t.columns.placed}</th>
              <th>{t.columns.under}</th>
              <th>{t.columns.gap}</th>
              <th>{t.columns.outcome}</th>
              <th>{t.columns.note}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.kind}-${r.id}-${i}`} className={rowClass(r)}>
                <td>{t.kinds[r.kind] ?? r.kind}</td>
                <td>{r.id}</td>
                <td>
                  <code>{r.className}</code>
                </td>
                <td className="num">{fmt(r.placed)}</td>
                <td className="num">{r.kind === 'force' ? '' : `${r.under.toFixed(1)} m`}</td>
                <td className="num">
                  {r.kind === 'force' || r.kind === 'point'
                    ? `${Math.round(r.gap)} m`
                    : `${r.gap > 0 ? '+' : ''}${r.gap.toFixed(1)} m`}
                </td>
                <td>{t.outcomes[r.outcome] ?? r.outcome}</td>
                <td className="muted small">
                  {[r.note, holder(r, t.stances)].filter(Boolean).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
